import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import { notificar } from '../utils/notify';
import '../styles/SpedContabil.css';

const SpedContabil = () => {
  const [empresas, setEmpresas] = useState([]);
  const [estatisticas, setEstatisticas] = useState({});
  const [estatisticasPorOperador, setEstatisticasPorOperador] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState('');

  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroOperador, setFiltroOperador] = useState('');
  const [filtroBusca, setFiltroBusca] = useState('');
  const [filtroObrigatoriedade, setFiltroObrigatoriedade] = useState('');
  
  const [modalEdicao, setModalEdicao] = useState({ show: false, empresa: null });
  const [modalEntrega, setModalEntrega] = useState({ show: false, empresa: null });
  const [modalHistorico, setModalHistorico] = useState({ show: false, empresa: null, historico: [] });
  const [modalHistoricoCompleto, setModalHistoricoCompleto] = useState({ show: false, historico: [] });
  
  const [formData, setFormData] = useState({
    obrigatoriedade_sped: '',
    obs_sped: '',
    encerramento_sped: '',
    dfs: ''
  });

  const [formEntrega, setFormEntrega] = useState({
    ano_competencia: new Date().getFullYear() - 1,
    numero_sped: '',
    observacao: ''
  });

  const CORES = {
    enviado: '#28a745',
    pendente: '#dc3545',
    naoObrigatorio: '#6c757d'
  };

  const verificarPendencia = (empresa) => {
    if (empresa.obrigatoriedade_sped?.toLowerCase() !== 'sim') return false;
    const anoAtual = new Date().getFullYear();
    const dataLimite = new Date(anoAtual, 5, 30);
    const hoje = new Date();
    const ultimoAnoEntregue = empresa.ultimo_ano_entregue || 0;
    if (ultimoAnoEntregue < (anoAtual - 1) && hoje <= dataLimite) {
      return true;
    }
    return false;
  };

  const carregarDados = useCallback(async () => {
    setCarregando(true);
    const accessToken = localStorage.getItem('access');
    try {
      const [responseSped, responseStats] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE}/api/sped-contabil/`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        }),
        axios.get(`${process.env.REACT_APP_API_BASE}/api/sped-contabil-estatisticas/`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        })
      ]);
      if (responseSped.data.success) {
        setEmpresas(responseSped.data.empresas);
        setEstatisticas(responseSped.data.estatisticas);
      }
      if (responseStats.data.success) {
        setEstatisticasPorOperador(responseStats.data.estatisticas);
      }
      setCarregando(false);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setMensagem('Erro ao carregar dados de SPED');
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const empresasFiltradas = empresas.filter(emp => {
    const matchStatus = !filtroStatus || emp.status_sped === filtroStatus;
    const matchOperador = !filtroOperador || emp.operador === filtroOperador;
    const matchBusca = !filtroBusca || 
      emp.empresa?.toLowerCase().includes(filtroBusca.toLowerCase()) ||
      String(emp.numero_dominio).includes(filtroBusca);
    const matchObrigatoriedade = !filtroObrigatoriedade || emp.obrigatoriedade_sped === filtroObrigatoriedade;
    return matchStatus && matchOperador && matchBusca && matchObrigatoriedade;
  });

  const abrirModalEdicao = (empresa) => {
    setModalEdicao({ show: true, empresa });
    setFormData({
      obrigatoriedade_sped: empresa.obrigatoriedade_sped || '',
      obs_sped: empresa.obs_sped || '',
      encerramento_sped: empresa.encerramento_sped || '',
      dfs: empresa.dfs || ''
    });
  };

  const abrirModalEntrega = (empresa) => {
    setModalEntrega({ show: true, empresa });
    setFormEntrega({
      ano_competencia: new Date().getFullYear() - 1,
      numero_sped: '',
      observacao: ''
    });
  };

  const abrirModalHistorico = async (empresa) => {
    const accessToken = localStorage.getItem('access');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/sped-contabil/historico/${empresa.numero_dominio}/`,
        { headers: { Authorization: `Bearer ${accessToken}` }}
      );
      setModalHistorico({ show: true, empresa, historico: response.data.historico });
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setMensagem('Erro ao carregar histórico');
    }
  };

  const abrirModalHistoricoCompleto = async () => {
    const accessToken = localStorage.getItem('access');
    setCarregando(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/sped-contabil/historico-completo/`,
        { headers: { Authorization: `Bearer ${accessToken}` }}
      );
      setModalHistoricoCompleto({ show: true, historico: response.data.historico });
      setCarregando(false);
    } catch (error) {
      console.error('Erro ao carregar histórico completo:', error);
      setMensagem('Erro ao carregar histórico completo');
      setCarregando(false);
    }
  };

  const salvarEdicao = async () => {
    const accessToken = localStorage.getItem('access');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/sped-contabil/${modalEdicao.empresa.numero_dominio}/`,
        formData,
        { headers: { Authorization: `Bearer ${accessToken}` }}
      );
      if (response.data.success) {
        setMensagem('SPED atualizado com sucesso!');
        setModalEdicao({ show: false, empresa: null });
        carregarDados();
        setTimeout(() => setMensagem(''), 3000);
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setMensagem('Erro ao atualizar SPED');
    }
  };

  const registrarEntrega = async () => {
    const accessToken = localStorage.getItem('access');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/sped-contabil/entrega/`,
        {
          numero_dominio: modalEntrega.empresa.numero_dominio,
          ano_competencia: formEntrega.ano_competencia,
          numero_sped: formEntrega.numero_sped,
          observacao: formEntrega.observacao
        },
        { headers: { Authorization: `Bearer ${accessToken}` }}
      );
      if (response.data.success) {
        setMensagem('Entrega registrada com sucesso!');
        setModalEntrega({ show: false, empresa: null });
        carregarDados();
        setTimeout(() => setMensagem(''), 3000);
      }
    } catch (error) {
      console.error('Erro ao registrar entrega:', error);
      setMensagem('Erro ao registrar entrega');
    }
  };

  const operadoresUnicos = [...new Set(empresas.map(e => e.operador).filter(Boolean))].sort();
  const dadosGraficoStatus = [
    { name: 'Enviados', value: estatisticas.enviados || 0, color: CORES.enviado },
    { name: 'Pendentes', value: estatisticas.pendentes || 0, color: CORES.pendente },
    { name: 'Não Obrigatório', value: estatisticas.nao_obrigatorios || 0, color: CORES.naoObrigatorio }
  ].filter(item => item.value > 0);

  if (carregando) {
    return (
      <div className="sped-contabil-page">
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="mt-3">Carregando dados de SPED...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sped-contabil-page">
      <div className="container-fluid py-4">
        <div className="header-section mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="titulo-pagina">
                <i className="fas fa-file-alt header-icon me-3"></i>
                SPED Contábil
              </h1>
              <p className="page-subtitle">Gerenciamento de SPEDs Contábeis</p>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-info" onClick={abrirModalHistoricoCompleto}>
                <i className="fas fa-history me-2"></i>
                Histórico Completo
              </button>
              <button className="btn btn-outline-primary" onClick={carregarDados}>
                <i className="fas fa-sync-alt me-2"></i>
                Atualizar Dados
              </button>
            </div>
          </div>
        </div>
        {/* Mensagens agora são exibidas como toast (react-toastify) */}
        <div className="row g-4 mb-4">
          <div className="col-xl-3 col-md-6">
            <div className="kpi-card kpi-total">
              <div className="kpi-icon"><i className="fas fa-building"></i></div>
              <div className="kpi-content">
                <h3 className="kpi-value">{estatisticas.total || 0}</h3>
                <p className="kpi-label">Total de Empresas</p>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6">
            <div className="kpi-card kpi-info">
              <div className="kpi-icon"><i className="fas fa-clipboard-check"></i></div>
              <div className="kpi-content">
                <h3 className="kpi-value">{estatisticas.obrigatorias || 0}</h3>
                <p className="kpi-label">SPED Obrigatório</p>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6">
            <div className="kpi-card kpi-success">
              <div className="kpi-icon"><i className="fas fa-check-circle"></i></div>
              <div className="kpi-content">
                <h3 className="kpi-value">{estatisticas.enviados || 0}</h3>
                <p className="kpi-label">SPEDs Enviados</p>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6">
            <div className="kpi-card kpi-danger">
              <div className="kpi-icon"><i className="fas fa-exclamation-triangle"></i></div>
              <div className="kpi-content">
                <h3 className="kpi-value">{estatisticas.pendentes || 0}</h3>
                <p className="kpi-label">SPEDs Pendentes</p>
              </div>
            </div>
          </div>
        </div>
        {estatisticas.obrigatorias > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">
                      <i className="fas fa-chart-line me-2 text-primary"></i>
                      Taxa de Cumprimento de SPED
                    </h6>
                    <span className={`badge fs-6 ${
                      estatisticas.taxa_cumprimento >= 90 ? 'bg-success' :
                      estatisticas.taxa_cumprimento >= 70 ? 'bg-warning' : 'bg-danger'
                    }`}>
                      {estatisticas.taxa_cumprimento}%
                    </span>
                  </div>
                  <div className="progress" style={{ height: '25px' }}>
                    <div className={`progress-bar ${estatisticas.taxa_cumprimento >= 90 ? 'bg-success' : estatisticas.taxa_cumprimento >= 70 ? 'bg-warning' : 'bg-danger'}`} style={{ width: `${estatisticas.taxa_cumprimento}%` }}>
                      {estatisticas.enviados} de {estatisticas.obrigatorias} enviados
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="row g-4 mb-4">
          <div className="col-lg-6">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-white">
                <h5 className="card-title mb-0">
                  <i className="fas fa-chart-pie me-2 text-primary"></i>
                  Distribuição de Status
                </h5>
              </div>
              <div className="card-body">
                {dadosGraficoStatus.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={dadosGraficoStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={60} paddingAngle={3} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                        {dadosGraficoStatus.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [value, 'Empresas']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="fas fa-inbox fa-3x mb-3"></i>
                    <p>Nenhum dado disponível</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-white">
                <h5 className="card-title mb-0">
                  <i className="fas fa-chart-bar me-2 text-primary"></i>
                  SPEDs por Operador
                </h5>
              </div>
              <div className="card-body">
                {estatisticasPorOperador.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={estatisticasPorOperador} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="operador" angle={-45} textAnchor="end" height={80} interval={0} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="enviados" name="Enviados" fill={CORES.enviado} stackId="a" />
                      <Bar dataKey="pendentes" name="Pendentes" fill={CORES.pendente} stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="fas fa-inbox fa-3x mb-3"></i>
                    <p>Nenhum dado disponível</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="filtros-section mb-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-search me-2 text-primary"></i>Buscar
                  </label>
                  <input type="text" className="form-control" placeholder="Empresa ou Nº Domínio..." value={filtroBusca} onChange={(e) => setFiltroBusca(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-info-circle me-2 text-primary"></i>Status
                  </label>
                  <select className="form-select" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
                    <option value="">Todos</option>
                    <option value="Enviado">Enviado</option>
                    <option value="Pendente">Pendente</option>
                    <option value="Não Obrigatório">Não Obrigatório</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-user me-2 text-primary"></i>Operador
                  </label>
                  <select className="form-select" value={filtroOperador} onChange={(e) => setFiltroOperador(e.target.value)}>
                    <option value="">Todos</option>
                    {operadoresUnicos.map(op => (<option key={op} value={op}>{op}</option>))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-clipboard-check me-2 text-primary"></i>Obrigatoriedade
                  </label>
                  <select className="form-select" value={filtroObrigatoriedade} onChange={(e) => setFiltroObrigatoriedade(e.target.value)}>
                    <option value="">Todos</option>
                    <option value="Sim">Obrigatório</option>
                    <option value="Não">Não Obrigatório</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <button className="btn btn-secondary w-100" onClick={() => { setFiltroBusca(''); setFiltroStatus(''); setFiltroOperador(''); setFiltroObrigatoriedade(''); }}>
                    <i className="fas fa-eraser me-2"></i>Limpar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="card shadow-sm">
          <div className="card-header bg-white d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <i className="fas fa-table me-2 text-primary"></i>Lista de Empresas
            </h5>
            <span className="badge bg-primary fs-6">{empresasFiltradas.length} empresa(s)</span>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>Nº Domínio</th>
                    <th>Empresa</th>
                    <th>Operador</th>
                    <th>Regime</th>
                    <th className="text-center">Obrigatório</th>
                    <th>Último Ano</th>
                    <th>Data Limite</th>
                    <th>DFS</th>
                    <th>Encerramento</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {empresasFiltradas.length > 0 ? (
                    empresasFiltradas.map((empresa, index) => {
                      const mostrarDataLimite = empresa.obrigatoriedade_sped?.toLowerCase() === 'sim' && 
                                                 (!empresa.ultimo_ano_entregue || empresa.ultimo_ano_entregue === 0);
                      return (
                        <tr key={index} className={verificarPendencia(empresa) ? 'table-warning' : ''}>
                          <td><code>{empresa.numero_dominio}</code></td>
                          <td className="fw-semibold">{empresa.empresa}</td>
                          <td><span className="badge bg-info">{empresa.operador || 'N/A'}</span></td>
                          <td>{empresa.regime || '-'}</td>
                          <td className="text-center">
                            <span className={`badge ${empresa.obrigatoriedade_sped?.toLowerCase() === 'sim' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                              {empresa.obrigatoriedade_sped || 'Não'}
                            </span>
                          </td>
                          <td>
                            {empresa.ultimo_ano_entregue > 0 ? (
                              <small className="text-success">
                                <i className="fas fa-check-circle me-1"></i>{empresa.ultimo_ano_entregue}
                              </small>
                            ) : (<small className="text-muted">-</small>)}
                          </td>
                          <td>
                            {mostrarDataLimite ? (
                              <small className="text-danger fw-bold">
                                <i className="fas fa-calendar-times me-1"></i>30/06/2026
                              </small>
                            ) : (<small className="text-muted">-</small>)}
                          </td>
                          <td>
                            <span className={`badge ${
                              empresa.dfs === 'Concluídas' ? 'bg-success' :
                              empresa.dfs === 'A fazer' ? 'bg-danger' :
                              empresa.dfs === 'Sem movimentação' ? 'bg-secondary' :
                              empresa.dfs === 'Só BP e DRE' ? 'bg-info' :
                              empresa.dfs === 'Outro colaborador' ? 'bg-warning text-dark' : 'bg-light text-dark'
                            }`}>
                              {empresa.dfs || '-'}
                            </span>
                          </td>
                          <td>{empresa.encerramento_sped || '-'}</td>
                          <td className="text-center">
                            <button className="btn btn-sm btn-outline-primary me-1" onClick={() => abrirModalEdicao(empresa)} title="Editar SPED">
                              <i className="fas fa-edit"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-success me-1" onClick={() => abrirModalEntrega(empresa)} title="Registrar Entrega">
                              <i className="fas fa-upload"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-info me-1" onClick={() => abrirModalHistorico(empresa)} title="Histórico">
                              <i className="fas fa-history"></i>
                            </button>
                            {empresa.drive_cliente && (
                              <a href={empresa.drive_cliente} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary" title="Abrir Drive">
                                <i className="fas fa-folder-open"></i>
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center text-muted py-4">
                        <i className="fas fa-search fa-2x mb-2"></i>
                        <p>Nenhuma empresa encontrada</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {modalEdicao.show && (
          <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-edit me-2"></i>Editar SPED - {modalEdicao.empresa?.empresa}</h5>
                  <button type="button" className="btn-close" onClick={() => setModalEdicao({ show: false, empresa: null })}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Obrigatoriedade SPED</label>
                      <select className="form-select" value={formData.obrigatoriedade_sped} onChange={(e) => setFormData({...formData, obrigatoriedade_sped: e.target.value})}>
                        <option value="">Selecione...</option>
                        <option value="Sim">Sim</option>
                        <option value="Não">Não</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Encerramento</label>
                      <select className="form-select" value={formData.encerramento_sped} onChange={(e) => setFormData({...formData, encerramento_sped: e.target.value})}>
                        <option value="">Selecione...</option>
                        <option value="Concluído">Concluído</option>
                        <option value="Inativa">Inativa</option>
                        <option value="Pendente">Pendente</option>
                        <option value="Outro contador">Outro contador</option>
                      </select>
                    </div>
                    <div className="col-md-12">
                      <label className="form-label fw-semibold">DFS</label>
                      <select className="form-select" value={formData.dfs} onChange={(e) => setFormData({...formData, dfs: e.target.value})}>
                        <option value="">Selecione...</option>
                        <option value="Outro colaborador">Outro colaborador</option>
                        <option value="Só BP e DRE">Só BP e DRE</option>
                        <option value="Sem movimentação">Sem movimentação</option>
                        <option value="A fazer">A fazer</option>
                        <option value="Concluídas">Concluídas</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-semibold">Observações</label>
                      <textarea className="form-control" rows="4" value={formData.obs_sped} onChange={(e) => setFormData({...formData, obs_sped: e.target.value})}></textarea>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setModalEdicao({ show: false, empresa: null })}>Cancelar</button>
                  <button type="button" className="btn btn-primary" onClick={salvarEdicao}>Salvar</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {modalEntrega.show && (
          <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-upload me-2"></i>Registrar Entrega - {modalEntrega.empresa?.empresa}</h5>
                  <button type="button" className="btn-close" onClick={() => setModalEntrega({ show: false, empresa: null })}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Ano de Competência</label>
                    <select className="form-select" value={formEntrega.ano_competencia} onChange={(e) => setFormEntrega({...formEntrega, ano_competencia: parseInt(e.target.value)})}>
                      {[...Array(10)].map((_, i) => {
                        const ano = new Date().getFullYear() - i;
                        return <option key={ano} value={ano}>{ano}</option>;
                      })}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Número do SPED</label>
                    <input type="text" className="form-control" placeholder="Ex: 123456789" value={formEntrega.numero_sped} onChange={(e) => setFormEntrega({...formEntrega, numero_sped: e.target.value})} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Observação</label>
                    <textarea className="form-control" rows="3" value={formEntrega.observacao} onChange={(e) => setFormEntrega({...formEntrega, observacao: e.target.value})}></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setModalEntrega({ show: false, empresa: null })}>Cancelar</button>
                  <button type="button" className="btn btn-success" onClick={registrarEntrega}>Registrar</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {modalHistorico.show && (
          <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-history me-2"></i>Histórico de Entregas - {modalHistorico.empresa?.empresa}</h5>
                  <button type="button" className="btn-close" onClick={() => setModalHistorico({ show: false, empresa: null, historico: [] })}></button>
                </div>
                <div className="modal-body">
                  {modalHistorico.historico.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Ano</th>
                            <th>Número SPED</th>
                            <th>Data Entrega</th>
                            <th>Usuário</th>
                            <th>Observação</th>
                          </tr>
                        </thead>
                        <tbody>
                          {modalHistorico.historico.map((h, i) => (
                            <tr key={i}>
                              <td><strong>{h.ano_competencia}</strong></td>
                              <td><code>{h.numero_sped || '-'}</code></td>
                              <td>{new Date(h.data_entrega).toLocaleDateString('pt-BR')}</td>
                              <td>{h.usuario}</td>
                              <td>{h.observacao || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (<p className="text-center text-muted">Nenhuma entrega registrada</p>)}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setModalHistorico({ show: false, empresa: null, historico: [] })}>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {modalHistoricoCompleto.show && (
          <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-history me-2"></i>
                    Histórico Completo de Entregas de SPED
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setModalHistoricoCompleto({ show: false, historico: [] })}></button>
                </div>
                <div className="modal-body">
                  {modalHistoricoCompleto.historico.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover table-sm">
                        <thead className="table-dark sticky-top">
                          <tr>
                            <th>Empresa</th>
                            <th>Nº Domínio</th>
                            <th>Ano</th>
                            <th>Número SPED</th>
                            <th>Data Entrega</th>
                            <th>Usuário</th>
                            <th>Observação</th>
                          </tr>
                        </thead>
                        <tbody>
                          {modalHistoricoCompleto.historico.map((h, i) => (
                            <tr key={i}>
                              <td className="fw-semibold">{h.empresa}</td>
                              <td><code>{h.numero_dominio}</code></td>
                              <td><strong>{h.ano_competencia}</strong></td>
                              <td><code>{h.numero_sped || '-'}</code></td>
                              <td>{new Date(h.data_entrega).toLocaleDateString('pt-BR')}</td>
                              <td><span className="badge bg-info">{h.usuario}</span></td>
                              <td>{h.observacao || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center text-muted py-5">
                      <i className="fas fa-inbox fa-3x mb-3"></i>
                      <p>Nenhuma entrega registrada no sistema</p>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <span className="me-auto text-muted">
                    <strong>{modalHistoricoCompleto.historico.length}</strong> entrega(s) registrada(s)
                  </span>
                  <button type="button" className="btn btn-secondary" onClick={() => setModalHistoricoCompleto({ show: false, historico: [] })}>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpedContabil;